import Dexie from 'dexie';
import { q1CacheFunctions } from './q1-core-functions';
import { pollUntil } from './polling-helpers.js';

const CACHE_DB_NAME = 'q1-cache-db';
const CACHE_DB_VERSION = 1;
const db = new Dexie(CACHE_DB_NAME);
db.version(CACHE_DB_VERSION).stores({
  cacheStore: 'id, defName, refreshedAt' // Indexes for queries and sorting
});

const FRESH_STATI = ['created', 'rendered'];
const MAX_POLL_DURATION = 60; // in seconds

/**
 * The caches included in a standard app-level refresh. They will get triggered in the order
 * they are listed here.
 * 
 * Format: `[cacheId, maxAge, visibility]`
 * - `cacheId`: The cache id to refresh
 * - `maxAge`: The max age (in minutes) considered client-side stale
 * - `personas`: An array of app personas that should trigger the refresh.
 *   Based on appPersona() from q1-home.
 */
const APP_STANDARD_CACHES = [
  ['all-places-tree', 60, ['public', 'network', 'contributor', 'foundation', 'steward']],
  ['all-guilds-public-index', 60, ['public']],
  ['all-guilds-network-index', 15, ['network', 'contributor']],
  ['all-guilds-index', 5, ['foundation', 'steward']],
  ['all-projects-public-index', 60, ['public']],
  ['all-projects-network-index', 15, ['network', 'contributor']],
  ['all-projects-index', 5, ['foundation', 'steward']],
  ['app-stats', 60, ['public', 'network', 'contributor', 'foundation', 'steward']],
  ['all-guilds-search', 10, ['network', 'contributor', 'foundation', 'steward']],
  ['all-projects-search', 10, ['network', 'contributor', 'foundation', 'steward']],
];

/**
 * # Cache Manager
 * 
 * Manages the caching of data in the client, including the retrieval of cache records from the
 * backend and the polling of the backend until a cache is rebuilt.
 * 
 * ## Usage
 * 
 * `getCache()` is the primary method to get a cache record. It will return the local DB cache 
 * record right away and trigger a refresh if the cache is client-side stale. The returned 
 * object will include a `refreshComplete` promise that will resolve with the new cache content 
 * once it is refreshed and/or rebuilt.
 * 
 * `getCaches()` is a wrapper around `getCache()` that simplifies querying for multiple caches
 * in parallel. It works the same as `getCache()`, but returns an array of cache records and groups
 * the promises into a single promise, handling parallel resolution internally.
 * 
 * `refreshLocalCache()` is used by `getCache()` under the hood but can be used directly in order
 * to force a refresh of the local cache record with the latest version from the backend, including
 * waiting for a rebuild if the cache is server-side stale.
 * 
 * `getRemoteCache()` is used by `refreshLocalCache()` under the hood but can be used directly in
 * order to query the backend for the latest version of a cache record, including waiting for a
 * rebuild if the cache is server-side stale. This method does not update the local DB.
 * 
 * `markCacheStale()` is used to mark a cache as stale in the local DB. This will trigger a refresh
 * of the cache the next time it is requested. >> FIXME: NOT IMPLEMENTED YET
 * 
 * ## Notes on Staleness
 * 
 * The cache manager is designed to handle two types of cache staleness:
 * - **Client-Side Stale**: The cache is stale because the local copy is out of date. This triggers
 *   a REFRESH of the cache from the backend.
 * - **Server-Side Stale**: The cache is stale because the backend needs to rebuild it. This 
 *   triggers a REBUILD of the cache in the DB functions on the backend and causes the 
 *   cache manager to poll the backend until the cache is fresh.
 * 
 * The distinction between these two types of staleness should be largely irrelevant to the 
 * consumer of the cache manager, but it's helpful to understand the differences and the meaning 
 * of the terms 'refresh' and 'rebuild' in the context of the cache manager.
 * 
 */
export class CacheManager {

  constructor() {
    this._resetInitParams();

    // FIXME >> Will do other stuff later
    this._resolveInit();
  }

  _resetInitParams() {
    this.ready = false;
    this.initComplete = new Promise((resolve, reject) => {
      this._resolveInit = resolve;
      this._rejectInit = reject;
    });
  }

  /**
   * Queries the local DB for the specified cache record and returns it right away.
   * If the cache record is client-side stale (based on the provided `maxAge` value),
   * this will trigger a refresh, using `getRemoteCache()` to get the latest version.
   * 
   * Either getBody or getData must be true.
   * 
   * If `isStale` is true: The current version is returned, then you can use the `refreshComplete` 
   * promise to get the new cache content once it is refreshed and/or rebuilt. 
   * If a rebuild IS needed, this promise will be held until the full rebuild is complete. 
   * (See comments at top of doc for more info on staleness.)
   * 
   * If `isNew` is true: The cache was not found in the local DB. Empty body and data is returned,
   * and the `refreshComplete` promise will resolve with the new cache content once it is fetched.
   * An error will be thrown if the cache is not found in the backend.
   * 
   * @param {String} cacheId - Required. Cache id to get.
   * @param {String} maxAge - Required. Max age (in minutes) considered client-side stale.
   * @param {Boolean} getBody - Defaults to true. If true, the body the cache will be included.
   * @param {Boolean} getData - Defaults to true. If true, the data the cache will be included.
   * 
   * @returns {Promise<Object>} - Resolves to an object with the following structure:
   * ```
   * {
   *   body: <String>, // if specified
   *   data: <Object>, // if specified
   *   isNew: <Boolean>, // true if the cache was not found in the DB
   *   isStale: <Boolean>, // true if cache is client-side stale (based on specified `maxAge`)
   *   isNewOrStale: <Boolean>, // OR combination of `isNew` and `isStale`
   *   refreshedAt: <Date>, // timestamp of the last refresh (null if cache is new)
   *   refreshComplete: <Promise>, // only present if `isStale`, resolves w/ new cache content
   * }
   * ```
   */
  async getCache(cacheId, maxAge, getBody = true, getData = true) {
    const localCache = await this._getCacheFromDb(cacheId);
    const maxAgeMs = maxAge * 60 * 1000;

    const [isNew, isStale] = [
      !localCache,
      localCache && ((Date.now() - localCache.refreshedAt) > maxAgeMs)
    ];

    let cache = {
      body: localCache?.body || (getBody ? {} : undefined),
      data: localCache?.data || (getData ? {} : undefined),
      refreshedAt: localCache?.refreshedAt ? new Date(localCache.refreshedAt) : null,
      isNew,
      isStale,
      isNewOrStale: isNew || isStale,
    };

    // If no local copy OR client-side stale, refresh the local copy.
    // Store a promise on the cache object that will resolve with the new cache content.
    if (cache.isNewOrStale) cache.refreshComplete =
      this.refreshLocalCache(cacheId, getBody, getData);

    return cache;
  }

  /**
   * Wrapper around `getCache()` which queries for multiple caches in a single call and groups
   * them into a single set of return promises.
   * 
   * Queries the local DB for the specified cache records and returns them right away.
   * If the cache records are client-side stale (based on the provided `maxAge` value),
   * this will trigger a refresh, using `getRemoteCache()` to get the latest version.
   * 
   * Using this wrapper will set getBody and getData to true.
   * 
   * If `anyAreNewOrStale` is true: The current versions are returned, then you can use the 
   * `refreshComplete` promise to get the new cache content once it is refreshed and/or rebuilt. 
   * If a rebuild IS needed, this promise will be held until the full rebuild is complete. 
   * (See comments at top of doc for more info on staleness.)
   * 
   * @param {String[]} cacheIds - Required. Cache ids to get.
   * @param {String} maxAge - Required. Max age (in minutes) considered client-side stale.
   * 
   * @returns {Promise<Object>} - Resolves to an object with the following structure:
   * ```
   * {
   *   caches: <Array>, // array of cache objects with keys:
   *     `body`, `data`, `isNew`, `isStale`, `isNewOrStale`, `refreshedAt`
   *   anyAreNewOrStale: <Boolean>, // True if at least one cache is new or stale.
   *   refreshesComplete: <Promise>, // only present if `anyAreNewOrStale`, resolves w/ array of
   *     new caches once all refreshes are complete.
   * }
   * ```
   */
  async getCaches(cacheIds, maxAge) {
    const cachePromises = cacheIds.map((cacheId) => this.getCache(cacheId, maxAge));
    const caches = await Promise.all(cachePromises);

    const anyAreNewOrStale = caches.some((cache) => cache.isNewOrStale);

    return {
      caches,
      anyAreNewOrStale,
      refreshesComplete: anyAreNewOrStale
        ? Promise.all(caches.map((cache) => cache.isNewOrStale ? cache.refreshComplete : cache))
        : null,
    };
  }

  /**
   * Refreshes the local cache record with the latest version from the backend, then returns 
   * the latest version.
   * 
   * If the cache is server-side stale, this will trigger a server-side cache rebuild
   * and this method will start polling the backend until the cache is rebuilt. The promise
   * returned by this method will be held until the full rebuild is complete.
   * 
   * @param {String} cacheId - Required. Cache id to refresh.
   * @param {Boolean} getBody - Defaults to true. If true, the body the cache will be included.
   * @param {Boolean} getData - Defaults to true. If true, the data the cache will be included.
   * 
   * @returns {Promise<Object>} - Resolves to an object with the following structure:
   * ```
   * {
   *   body: <String>, // if specified
   *   data: <Object>, // if specified
   *   isStale: <Boolean>, // always false
   *   refreshedAt: <Date>, // timestamp of the last refresh (now)
   * }
   * ```
   */
  async refreshLocalCache(cacheId, getBody = true, getData = true) {
    let remoteCache = await this.getRemoteCache(cacheId, getBody, getData);
    
    // If the cache is server-side stale, wait for the rebuild to complete.
    if (remoteCache.isStale) remoteCache = await remoteCache.rebuildComplete;

    const refreshedAt = Date.now();

    // Put the new cache in the local DB.
    await this._putCacheInDb({
      cacheId,
      data: remoteCache.data,
      body: remoteCache.body,
      refreshedAt,
    });

    return {
      ...remoteCache,
      refreshedAt: new Date(refreshedAt),
    };
  }

  /**
   * Queries the backend for the latest version of the specified cache record.
   * If the cache is server-side stale, this will trigger a server-side cache rebuild 
   * and this method will start polling the backend until the cache is rebuilt.
   * 
   * Either getBody or getData must be true.
   * 
   * If `isStale` is true: Use the `rebuildComplete` promise to get the new cache content 
   * once it is rebuilt. (See comments at top of doc for more info.)
   * 
   * @param {String} cacheId - Required. Cache id to get.
   * @param {Boolean} getBody - Defaults to true. If true, the body the cache will be included.
   * @param {Boolean} getData - Defaults to true. If true, the data the cache will be included.
   * 
   * @returns {Promise<Object>} - Resolves to an object with the following structure:
   * ```
   * {
   *   body: <String>, // if specified
   *   data: <Object>, // if specified
   *   isStale: <Boolean>, // true if cache is server-side stale
   *   rebuildComplete: <Promise>, // only present if `isStale`, resolves w/ new cache content
   * }
   * ```
   */
  async getRemoteCache(cacheId, getBody = true, getData = true) {
    // NOTE: Don't capture the errors here
    const cacheResponse = await this._getCacheBackend(cacheId, getBody, getData);
    if (!cacheResponse) throw new Error(`Cache not found: ${cacheId}`);
    
    // Build cache return value and determine if it is 'server-side stale', meaning that our
    // request of the cache triggered the DB function to start REBUILDING it.
    const cache = {
      body: cacheResponse.body,
      data: cacheResponse.data,
      isStale: !FRESH_STATI.includes(cacheResponse.status),
    };

    // If server-side stale, start a poller to query until server-side REBUILD is complete.
    // Store a promise on the cache object that will resolve with the new cache content.
    if (cache.isStale) cache.rebuildComplete = 
      this.pollUntilCacheRebuilt(cacheId, getBody, getData);

    return cache;
  }

  /**
   * Polls the backend until the cache is rebuilt (meaning that it's at a FRESH_STATE).
   * Throws an error if `maxPollDuration` is reached.
   * 
   * @param {String} cacheId - Required. Cache id to poll.
   * @param {Boolean} getBody - Defaults to true. If true, the body of each cache will be included.
   * @param {Boolean} getData - Defaults to true. If true, the data of each cache will be included.
   * @param {Integer} maxPollDuration - Defaults to MAX_POLL_DURATION. The maximum duration 
   *   (in seconds) to poll before throwing an error.
   * 
   * @returns {Promise<Object>} - Resolves to an object with the following structure:
   * ```
   * {
   *   body: <String>,
   *   data: <Object>,
   *   isStale: false, // always false
   * }
   * ```
   */
  async pollUntilCacheRebuilt(
    cacheId, getBody = true, getData = true, maxPollDuration = MAX_POLL_DURATION
  ) {
    let rebuiltCache;

    const success = await pollUntil({
      isComplete: async () => {
        // NOTE: Don't capture the errors here
        const cache = await this._getCacheBackend(cacheId, getBody, getData);
        rebuiltCache = cache;
        
        if (!rebuiltCache) throw new Error(`Cache not found: ${cacheId}`);

        return FRESH_STATI.includes(rebuiltCache.status);
      },
      maxDuration: maxPollDuration,
    });

    if (!success) throw new Error('Cache refresh timed out');
    return {
      body: rebuiltCache.body,
      data: rebuiltCache.data,
      isStale: false,
    };
  }

  /**
   * Starts a standard background refresh of the app-level caches defined in APP_STANDARD_CACHES.
   * The `getCache()` method is used under the hood, so the process will only trigger a server
   * refresh if the cache is client-side stale according to the maxAge values specified in 
   * APP_STANDARD_CACHES.
   * 
   * @param {String} currentAppPersona - Required. The current app persona, used to select the
   *   relevant items from the APP_STANDARD_CACHES.
   *   Values: 'public', 'network', 'contributor', 'foundation', 'steward'
   * @param {Number} interval - Required. The interval (in seconds) used to stagger the refreshes 
   *   of the caches to avoid pulling down too much data at once. The first refresh will not kick
   *   off immediately, but will wait for the interval to pass.
   */
  startStandardBackgroundRefresh(currentAppPersona, interval) {
    let count = 1;
    APP_STANDARD_CACHES.forEach(([cacheId, maxAge, personas]) => {
      if (personas.includes(currentAppPersona)) {
        setTimeout(
          () => {
            this.getCache(cacheId, maxAge);
          },
          count * interval * 1000
        );
        count++;
      }
    });
  }

  /**
   * Internal wrapper around the backend function.
   * Returns the `cache` object from the response data.
   */
  async _getCacheBackend(cacheId, getBody, getData) {
    const getContent = [];
    if (getBody) getContent.push('body');
    if (getData) getContent.push('data');
    if (!getContent.length) throw new Error('Either getBody or getData must be true');

    // NOTE: Don't capture the errors here
    const response = await q1CacheFunctions.getCache({ cacheId, getContent });
    const caches = response.data.cache;

    return caches;
  }

  /**
   * Puts a cache record in the DB. If the cache already exists, it will be updated.
   * Returns true on success and false on failure. Does not throw errors.
   * 
   * @param {Object} params - Required.
   * @param {String} params.cacheId - Required. Cache id.
   * @param {Object} params.data - Optional. Data to store in the cache.
   * @param {String} params.body - Optional. Body to store in the cache.
   * @param {Integer} params.refreshedAt - Optional. Timestamp of the last refresh.
   * @returns 
   */
  async _putCacheInDb({ cacheId, data = {}, body = {}, refreshedAt = Date.now() }) {
    if (!cacheId) throw new Error('_putCacheInDb: cacheId is required');

    try {
      await db.cacheStore.put({
        id: cacheId,
        data,
        body,
        refreshedAt,
      });

      return true;
    } catch (error) {
      console.error('Failed to put cache in DB:', error);

      return false;
    }
  }

  async _getCacheFromDb(cacheId) {
    const cacheEntry = await db.cacheStore.get(cacheId);

    return cacheEntry;
  }
  
}
